import { useState, useEffect, ChangeEvent } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Avatar, 
  Button, 
  TextField, 
  Grid, 
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  PhotoCamera as PhotoCameraIcon, 
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { generateClient } from 'aws-amplify/api';
import { uploadData, getUrl } from 'aws-amplify/storage';
import { getAmpUser } from '../graphql/queries';
import { createAmpUser, updateAmpUser } from '../graphql/mutations';

const client = generateClient();

interface AmpUser {
  id: string;
  email: string;
  name?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  owner: string;
  sharedRouterPath?: string;
}

const Profile = () => {
  const { user } = useAuthenticator((context) => [context.user]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ampUser, setAmpUser] = useState<AmpUser | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const fetchProfile = async () => {
    if (!user || !user.userId) return;
    setLoading(true);
    try {
      // 1. Fetch AmpUser record
      const result = await client.graphql({
        query: getAmpUser,
        variables: { id: user.userId }
      }) as any;

      let profile = result.data.getAmpUser;

      // 2. If not found, create it
      if (!profile) {
        console.log('Profile not found, creating new one...');
        const createResult = await client.graphql({
          query: createAmpUser,
          variables: {
            input: {
              id: user.userId,
              email: user.signInDetails?.loginId || '',
              name: user.username || '',
            }
          }
        }) as any;
        profile = createResult.data.createAmpUser;
      }

      setAmpUser(profile);

      // 3. Fetch Avatar if exists (using sharedRouterPath as a placeholder for image key for now)
      if (profile.sharedRouterPath) {
        const urlResult = await getUrl({
          path: `public/avatars/${profile.sharedRouterPath}`,
        });
        setAvatarUrl(urlResult.url.toString());
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setSnackbar({ open: true, message: 'Failed to load profile', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [user]);

  const handleSave = async () => {
    if (!ampUser) return;
    setSaving(true);
    try {
      const { owner, createdAt, updatedAt, __typename, ...updateInput } = ampUser as any;
      await client.graphql({
        query: updateAmpUser,
        variables: { input: updateInput }
      });
      setSnackbar({ open: true, message: 'Profile saved successfully', severity: 'success' });
    } catch (error) {
      console.error('Error saving profile:', error);
      setSnackbar({ open: true, message: 'Failed to save profile', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    setSaving(true);
    try {
      const fileKey = `${user.userId}-${Date.now()}-${file.name}`;
      
      // 1. Upload to S3
      await uploadData({
        path: `public/avatars/${fileKey}`,
        data: file,
        options: {
          contentType: file.type,
        }
      }).result;

      // 2. Update AmpUser record with the key
      await client.graphql({
        query: updateAmpUser,
        variables: {
          input: {
            id: user.userId,
            sharedRouterPath: fileKey // Using this as placeholder for avatar key
          }
        }
      });

      // 3. Refresh profile
      await fetchProfile();
      setSnackbar({ open: true, message: 'Avatar updated successfully', severity: 'success' });
    } catch (error) {
      console.error('Error uploading avatar:', error);
      setSnackbar({ open: true, message: 'Failed to upload avatar', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof AmpUser) => (event: ChangeEvent<HTMLInputElement>) => {
    if (!ampUser) return;
    setAmpUser({ ...ampUser, [field]: event.target.value });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          User Profile
        </Typography>
        <Tooltip title="Refresh Profile">
          <IconButton onClick={fetchProfile} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Avatar
              src={avatarUrl || undefined}
              sx={{ width: 150, height: 150, mb: 2, border: '4px solid #005596' }}
            >
              {ampUser?.name?.charAt(0) || user?.username?.charAt(0)}
            </Avatar>
            <Button
              variant="outlined"
              component="label"
              startIcon={<PhotoCameraIcon />}
              disabled={saving}
              sx={{ mt: 1 }}
            >
              Upload Avatar
              <input hidden accept="image/*" type="file" onChange={handleAvatarUpload} />
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              ID: {user?.userId}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Display Name"
                  value={ampUser?.name || ''}
                  onChange={handleChange('name')}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Email"
                  value={ampUser?.email || ''}
                  disabled
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={ampUser?.phone || ''}
                  onChange={handleChange('phone')}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={ampUser?.address || ''}
                  onChange={handleChange('address')}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="City"
                  value={ampUser?.city || ''}
                  onChange={handleChange('city')}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="State"
                  value={ampUser?.state || ''}
                  onChange={handleChange('state')}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="ZIP"
                  value={ampUser?.zip || ''}
                  onChange={handleChange('zip')}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  sx={{ mt: 2, height: 48 }}
                >
                  {saving ? <CircularProgress size={24} color="inherit" /> : 'Save Profile Changes'}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile;
